/**
 * Handles Arabic text processing, particularly for extracting the first meaningful letter
 * @param {string} text - The Arabic text to process
 * @returns {object} - Object containing the first letter and trimmed text
 */
export const getFirstMeaningfulLetter = (text) => {
  if (!text || typeof text !== "string") {
    return { firstLetter: "", trimmedText: text || "" };
  }

  // Remove leading and trailing whitespace
  const cleanedText = text.trim();

  if (cleanedText.length === 0) {
    return { firstLetter: "", trimmedText: "" };
  }

  // Check if the text starts with "ال" (Arabic definite article)
  if (cleanedText.startsWith("ال") && cleanedText.length > 2) {
    // Skip "ال" and get the first letter after it
    const textAfterAl = cleanedText.substring(2);
    const firstLetter = textAfterAl.charAt(0);
    const trimmedText = textAfterAl;

    return {
      firstLetter: firstLetter,
      trimmedText: trimmedText,
      skippedAl: true,
    };
  }

  // If it doesn't start with "ال", just return the first character
  const firstLetter = cleanedText.charAt(0);
  const trimmedText = cleanedText;

  return {
    firstLetter: firstLetter,
    trimmedText: trimmedText,
    skippedAl: false,
  };
};

/**
 * Creates a hint text showing the first letter with blanks for the rest
 * @param {string} text - The original text
 * @returns {string} - Hint text with first letter and underscores
 */
export const createFirstLetterHint = (text) => {
  const { firstLetter, trimmedText, skippedAl } =
    getFirstMeaningfulLetter(text);

  if (!firstLetter) {
    return text;
  }

  // Create underscores for the remaining letters
  const remainingLength = trimmedText.length - 1;
  const underscores = "_".repeat(Math.max(0, remainingLength));

  // Add back "ال" if it was skipped, but show it normally
  const prefix = skippedAl ? "ال" : "";

  return `${prefix}${firstLetter}${underscores}`;
};

/**
 * Creates a pattern showing the structure of the answer (letters and spaces)
 * @param {string} text - The original text
 * @returns {string} - Pattern text showing word structure
 */
export const createAnswerPattern = (text) => {
  if (!text || typeof text !== "string") {
    return "";
  }

  const cleanedText = text.trim();
  if (cleanedText.length === 0) {
    return "";
  }

  // Replace each Arabic/Latin letter with underscore, keep spaces and punctuation
  const pattern = cleanedText
    .split("")
    .map((char) => {
      if (/[\u0600-\u06FF\u0750-\u077F]/.test(char)) {
        // Arabic letters
        return "_";
      } else if (/[a-zA-Z]/.test(char)) {
        // Latin letters
        return "_";
      } else if (/\s/.test(char)) {
        // Spaces
        return " ";
      } else {
        // Punctuation and numbers - keep as is
        return char;
      }
    })
    .join("");

  return pattern;
};

/**
 * Gets word and character count for the answer
 * @param {string} text - The original text
 * @returns {object} - Object with word count, character count, and pattern
 */
export const getAnswerStats = (text) => {
  if (!text || typeof text !== "string") {
    return { wordCount: 0, charCount: 0, pattern: "" };
  }

  const cleanedText = text.trim();
  const words = cleanedText.split(/\s+/).filter((word) => word.length > 0);
  const chars = cleanedText.replace(/\s/g, "").length;
  const pattern = createAnswerPattern(cleanedText);

  return {
    wordCount: words.length,
    charCount: chars,
    pattern: pattern,
  };
};

/**
 * Validates if a text is primarily Arabic
 * @param {string} text - Text to validate
 * @returns {boolean} - True if text contains Arabic characters
 */
export const isArabicText = (text) => {
  if (!text) return false;

  // Arabic Unicode range: \u0600-\u06FF
  const arabicRegex = /[\u0600-\u06FF]/;
  return arabicRegex.test(text);
};

const arabicTextUtils = {
  getFirstMeaningfulLetter,
  createFirstLetterHint,
  createAnswerPattern,
  getAnswerStats,
  isArabicText,
};

export default arabicTextUtils;
